import { FC, useState } from "react";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SaveIcon from "@mui/icons-material/Save";
import { Box, Button, Menu, MenuItem, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { EditIcon } from "../../../../assets/icons";
import { AppRoutes } from "../../../../common/routesUtils";
import { View } from "../../../../components/layout/View";
import { PageType } from "../../../../config/page.types";
import { CarbonSnapCalculateRequest } from "../../../../datasources/carbonsnap/io/requests";
import { CarbonSnapResultsResponse } from "../../../../datasources/carbonsnap/io/response";
import { ApiRoutes } from "../../../../datasources/networking/apiRoutes";
import { useReadResource } from "../../../../hooks/useResource";
import EmissionDetailsSection from "./components/EmissionDetailsSection";
import ResultsKPISection from "./components/ResultsKPISection";
import ResultsMapSection from "./components/ResultsMapSection";
import TransportDataSection from "./components/TransportDataSection";

const CarbonSnapResults: FC = () => {
	const { t } = useTranslation("whatIf.carbonsnap");
	const navigate = useNavigate();
	const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(null);

	// TODO: Get calculation request from location state or context
	// For now, using empty request - in real app this should come from composer
	const calculationRequest: CarbonSnapCalculateRequest = {
		departureDate: "2025-02-15",
		numberOfTrips: 10,
		weight: 500,
		weightUnit: "tonne",
		temperature: "ambient",
		calculationMode: "mixed",
		tripCode: "",
		nodes: [],
		arcs: [],
	};

	const results = useReadResource<CarbonSnapCalculateRequest, CarbonSnapResultsResponse>(
		{ route: ApiRoutes.CarbonSnapCalculate },
		{
			readMethod: "post",
		},
		calculationRequest
	);

	const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
		setExportAnchorEl(event.currentTarget);
	};

	const handleExportClose = () => {
		setExportAnchorEl(null);
	};

	const handleExportOption = (format: string) => {
		// TODO: Implement export functionality
		console.log(`Export as ${format}`);
		handleExportClose();
	};

	const handleSaveTrip = () => {
		// TODO: Implement save trip functionality
		console.log("Save trip");
	};

	const handleModify = () => {
		// TODO: Preserve current trip data for editing
		navigate(AppRoutes.WhatIfCarbonSnap);
	};

	if (!results.data) {
		return (
			<View pageType={PageType.WhatIfCarbonSnapResults}>
				<Box sx={{ p: 3 }}>
					<Typography>{t("results/page/loading")}</Typography>
				</Box>
			</View>
		);
	}

	return (
		<View pageType={PageType.WhatIfCarbonSnapResults}>
			<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
				<Typography variant="h5" component="h1">
					{t("results/page/title")}
				</Typography>
				<Stack direction="row" spacing={1}>
					<Button variant="text" startIcon={<EditIcon />} onClick={handleModify}>
						{t("results/actions/modify")}
					</Button>
					<Button variant="text" startIcon={<FileDownloadIcon />} onClick={handleExportClick}>
						{t("results/page/export")}
					</Button>
					<Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveTrip}>
						{t("results/page/save trip")}
					</Button>
					<Menu anchorEl={exportAnchorEl} open={Boolean(exportAnchorEl)} onClose={handleExportClose}>
						<MenuItem onClick={() => handleExportOption("pdf")}>
							{t("results/page/export formats/pdf")}
						</MenuItem>
						<MenuItem onClick={() => handleExportOption("excel")}>
							{t("results/page/export formats/excel")}
						</MenuItem>
						<MenuItem onClick={() => handleExportOption("csv")}>
							{t("results/page/export formats/csv")}
						</MenuItem>
						<MenuItem onClick={() => handleExportOption("json")}>
							{t("results/page/export formats/json")}
						</MenuItem>
					</Menu>
				</Stack>
			</Box>

			<Stack spacing={3}>
				<ResultsKPISection totalEmissions={results.data.totalEmissions} />
				<TransportDataSection transportData={results.data.transportData} />
				<ResultsMapSection route={results.data.route} />
				<EmissionDetailsSection
					arcDetails={results.data.arcDetails}
					countryDetails={results.data.countryDetails}
					vehicleDetails={results.data.vehicleDetails}
					route={results.data.route}
				/>
			</Stack>
		</View>
	);
};

export default CarbonSnapResults;
